import { intervalToDuration } from 'date-fns'
import { capitalisePlatform, platformName, upperFirst } from '@/lib/text'
import {
  asArray,
  asBoolean,
  asDate,
  asNumber,
  asObject,
  asRaw,
  asString,
  asStringArray,
  withoutNullish,
} from './guards'
import { GameInfo } from './game-info'
import { Media, MediaUpload } from './media'
import { Objective } from './objective'
import { User } from './user'

const WEEK_MS = 7 * 86_400_000
const STEAM_HEADER = 'https://cdn.akamai.steamstatic.com/steam/apps/{id}/header.jpg'

export interface CountdownParts {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export interface CountdownDisplay {
  digit: number
  text: 'days' | 'hours' | 'mins' | 'secs'
}

export class GiveawayType {
  id: number | null
  name: string | null
  created_at: Date | null
  owner_id: number | null
  owner: User | null
  first_entries: boolean

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.name = asString(d.name)
    this.created_at = asDate(d.created_at)
    this.owner_id = asNumber(d.owner_id)
    this.owner = asObject(d.owner) ? new User(d.owner) : null
    this.first_entries = asBoolean(d.first_entries, false) as boolean
  }
}

export class PrizeType {
  id: number | null
  name: string | null
  requires_key: boolean

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.name = asString(d.name)
    this.requires_key = asBoolean(d.requires_key, false) as boolean
  }
}

export class GiveawayGroup {
  id: number | null
  owner_id: number | null
  owner: User | null
  created_at: Date | null
  name: string | null
  count: number | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.owner_id = asNumber(d.owner_id)
    this.owner = asObject(d.owner) ? new User(d.owner) : null
    this.created_at = asDate(d.created_at)
    this.name = asString(d.name)
    this.count = asNumber(d.count)
  }
}

export class GiveawayObjective {
  id: number | null
  objective: Objective | null
  giveaway_id: number | null
  name: string | null
  objective_id: number | null
  entries_worth: number | null
  objective_data_uuid: string | null
  objective_data_name: string | null
  is_entered: boolean
  /** Referral options only: how many people the current user has referred. */
  referral_count: number
  /** When the organiser removed it; null while it is live. */
  removed_at: Date | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.objective = asObject(d.objective) ? new Objective(d.objective) : null
    this.giveaway_id = asNumber(d.giveaway_id)
    this.name = asString(d.name)
    this.objective_id = asNumber(d.objective_id)
    this.entries_worth = asNumber(d.entries_worth)
    this.objective_data_uuid = asString(d.objective_data_uuid)
    this.objective_data_name = asString(d.objective_data_name)
    this.is_entered = asBoolean(d.is_entered, false) as boolean
    this.referral_count = asNumber(d.referral_count) ?? 0
    this.removed_at = asDate(d.removed_at)
  }

  get is_referral(): boolean {
    return this.objective?.is_referral ?? false
  }

  /**
   * e.g. "Join Spawn World on Discord".
   *
   * The API stores the action and the platform lowercase; the original
   * interpolated them raw, so every entry option read "join x on discord".
   */
  get display_name(): string {
    if (this.is_referral) return upperFirst(this.name ?? 'Refer a friend')
    const platform = platformName(this.objective?.external_platform?.name)
    // A stored name wins, but it is hand-written and puts the platform last
    // ("join Loot Drive on discord"), where upperFirst never reaches it.
    if (this.name) return upperFirst(capitalisePlatform(this.name, platform))
    const action = this.objective?.endpoint_action
    if (!action || !platform) return upperFirst(this.objective_data_name ?? '')
    return upperFirst(
      `${action} ${this.objective_data_name ?? ''} on ${platform}`.replace(/\s+/g, ' ').trim(),
    )
  }

  /** Stable React key: new objectives have no id until the server assigns one. */
  get key(): string {
    return this.id !== null ? `id:${this.id}` : `uuid:${this.objective_data_uuid ?? ''}`
  }
}

export class GiveawayEntry {
  id: number | null
  user_id: number | null
  created_at: Date | null
  giveaway_id: number | null
  giveaway_objective_id: number | null
  is_complete: boolean | null
  step: number | null

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.user_id = asNumber(d.user_id)
    this.created_at = asDate(d.created_at)
    this.giveaway_id = asNumber(d.giveaway_id)
    this.giveaway_objective_id = asNumber(d.giveaway_objective_id)
    this.is_complete = asBoolean(d.is_complete)
    this.step = asNumber(d.step)
  }
}

/**
 * Port of constructors/giveaway.js.
 *
 * Two deliberate changes from the original:
 *
 *  1. `giveaway_objective` is always an array. The Vue version stored a
 *     `{ [id]: GiveawayObjective }` map when hydrating from the API but a plain
 *     array while composing a new giveaway, and `payload()` had to convert
 *     between them. Lookups by id go through `objectiveById()` instead.
 *
 *  2. The constructor no longer starts a `setInterval` to refresh the
 *     countdown. Every `Giveaway` the app built leaked one timer for the
 *     lifetime of the tab. `time_until_finish` is computed on read, and
 *     `useTicker()` drives the re-render.
 */
export class Giveaway {
  id: number | null
  name: string | null
  description: string | null
  terms: string | null
  cover_media_id: number | null
  cover_media: Media | null
  cover_media_upload: MediaUpload | null
  prizes: string[] | null
  game_info_id: number | null
  game_info: GameInfo | null
  giveaway_group_id: number | null
  giveaway_group: GiveawayGroup | null
  giveaway_type_id: number
  giveaway_type: GiveawayType | null
  prize_type_id: number | null
  prize_type: PrizeType | null
  slug: string | null
  /** Its own featured video; see featured_video for what to show. */
  video_url: string | null
  /** Its own gallery; see display_gallery for what to show. */
  gallery: string[]
  created_at: Date | null
  scheduled_at: Date
  finishes_at: Date
  ended_at: Date | null
  /** The live entry options — the ones shown and enterable. */
  giveaway_objective: GiveawayObjective[]
  /**
   * Options the organiser removed. Never shown, but entries already made
   * through them still count, so they are kept to total those up.
   */
  removed_objectives: GiveawayObjective[]
  owner_id: number | null
  owner: User | null
  giveaway_entry: GiveawayEntry[]
  /** Left out by the API for everyone but the owner when hide_entries is set. */
  total_entries: number | null
  /** The organiser chose not to show the entry count. */
  hide_entries: boolean
  /** First-come only: a key needs every entry option done, not just one. */
  require_all_objectives: boolean
  /** On the home page's featured list. Only admins can change it. */
  is_featured: boolean
  my_entries: number

  constructor(data: unknown = {}) {
    const d = asRaw(data)
    this.id = asNumber(d.id)
    this.name = asString(d.name)
    this.description = asString(d.description)
    this.terms = asString(d.terms)
    this.cover_media_id = asNumber(d.cover_media_id)
    this.cover_media = asObject(d.cover_media) ? new Media(d.cover_media) : null
    this.cover_media_upload = asObject(d.cover_media_upload)
      ? new MediaUpload(d.cover_media_upload)
      : null
    this.prizes = asArray<string>(d.prizes)
    this.game_info_id = asNumber(d.game_info_id)
    this.game_info = asObject(d.game_info) ? new GameInfo(d.game_info) : null
    this.giveaway_group_id = asNumber(d.giveaway_group_id)
    this.giveaway_group = asObject(d.giveaway_group) ? new GiveawayGroup(d.giveaway_group) : null
    this.giveaway_type_id = asNumber(d.giveaway_type_id) ?? 1
    this.giveaway_type = asObject(d.giveaway_type) ? new GiveawayType(d.giveaway_type) : null
    this.prize_type_id = asNumber(d.prize_type_id)
    this.prize_type = asObject(d.prize_type) ? new PrizeType(d.prize_type) : null
    this.slug = asString(d.slug)
    this.video_url = asString(d.video_url)
    this.gallery = asStringArray(d.gallery) ?? []
    this.created_at = asDate(d.created_at)
    this.scheduled_at = asDate(d.scheduled_at) ?? new Date()
    this.finishes_at = asDate(d.finishes_at) ?? new Date(Date.now() + WEEK_MS)
    this.ended_at = asDate(d.ended_at)
    this.owner_id = asNumber(d.owner_id)
    this.owner = asObject(d.owner) ? new User(d.owner) : null
    this.total_entries = asNumber(d.total_entries)
    this.hide_entries = d.hide_entries === true
    this.require_all_objectives = d.require_all_objectives === true
    this.is_featured = d.is_featured === true

    const objectives = (asArray(d.giveaway_objective) ?? []).map(
      (item) => new GiveawayObjective(item),
    )
    this.giveaway_objective = objectives.filter((objective) => objective.removed_at === null)
    this.removed_objectives = objectives.filter((objective) => objective.removed_at !== null)
    this.giveaway_entry = (asArray(d.giveaway_entry) ?? []).map((item) => new GiveawayEntry(item))

    // Fold the current user's entries back onto the objectives so the details
    // screen can render "already entered" without a second request.
    let mine = 0
    for (const entry of this.giveaway_entry) {
      const objective =
        this.objectiveById(entry.giveaway_objective_id) ??
        this.removed_objectives.find((removed) => removed.id === entry.giveaway_objective_id)
      if (!objective) continue
      // A referral option is never "done": each entry under it is one
      // person this user referred.
      if (objective.is_referral) objective.referral_count += 1
      else objective.is_entered = true
      mine += objective.entries_worth ?? 0
    }
    this.my_entries = mine
  }

  objectiveById(id: number | null): GiveawayObjective | undefined {
    if (id === null) return undefined
    return this.giveaway_objective.find((objective) => objective.id === id)
  }

  get display_my_entries(): number {
    return this.my_entries
  }

  get display_total_entries(): number {
    return this.total_entries ?? 0
  }

  /**
   * Whether to show the entry count. False when the organiser hid it and this
   * viewer isn't them (the API sent no count) -- "0 entries" would be wrong.
   */
  get shows_entry_count(): boolean {
    return !this.hide_entries || this.total_entries !== null
  }

  /** Cover art, preferring the Steam header the original hard-coded. */
  get display_cover(): Media {
    if (this.game_info?.steam_id) {
      return new Media({ src: STEAM_HEADER.replace('{id}', String(this.game_info.steam_id)) })
    }
    if (this.cover_media?.src) return this.cover_media
    if (this.game_info) return new Media({ src: this.game_info.display_cover_media })
    return new Media({ src: '' })
  }

  /** The giveaway's own video, or else its game's. Blank counts as unset. */
  get featured_video(): string | null {
    return this.video_url?.trim() || this.game_info?.video_url?.trim() || null
  }

  /** The giveaway's own gallery, or else its game's. */
  get display_gallery(): string[] {
    return this.gallery.length > 0 ? this.gallery : (this.game_info?.gallery ?? [])
  }

  get get_slug(): string {
    return this.slug ?? (this.id !== null ? String(this.id) : '')
  }

  /** Recomputed on every read; pair with `useTicker()` to animate. */
  get time_until_finish(): CountdownParts {
    const now = new Date()
    if (this.finishes_at.getTime() <= now.getTime()) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }
    const duration = intervalToDuration({ start: now, end: this.finishes_at })
    return {
      days: (duration.years ?? 0) * 365 + (duration.months ?? 0) * 30 + (duration.days ?? 0),
      hours: duration.hours ?? 0,
      minutes: duration.minutes ?? 0,
      seconds: duration.seconds ?? 0,
    }
  }

  /** The largest non-zero unit, as shown on the cards and entry strips. */
  get display_time_until_finish(): CountdownDisplay {
    const { days, hours, minutes, seconds } = this.time_until_finish
    if (days > 0) return { digit: days, text: 'days' }
    if (hours > 0) return { digit: hours, text: 'hours' }
    if (minutes > 0) return { digit: minutes, text: 'mins' }
    if (seconds > 0) return { digit: seconds, text: 'secs' }
    return { digit: 0, text: 'secs' }
  }

  get has_ended(): boolean {
    return this.ended_at !== null || this.finishes_at.getTime() <= Date.now()
  }

  /** Body for POST/PUT /giveaway — nulls stripped, as the API expects. */
  payload(): Record<string, unknown> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { removed_objectives, ...fields } = this
    return withoutNullish({
      ...fields,
      giveaway_objective: this.giveaway_objective.map((objective) =>
        withoutNullish({ ...objective }),
      ),
    }) as Record<string, unknown>
  }

  /** Structural copy — the wizard mutates a draft between steps. */
  clone(): Giveaway {
    const next = new Giveaway()
    Object.assign(next, this)
    next.giveaway_objective = [...this.giveaway_objective]
    next.removed_objectives = [...this.removed_objectives]
    return next
  }
}

/**
 * Whether the giveaway is a "first entries" type (first come, first served).
 * Pass the loaded type list in the wizard: there only giveaway_type_id is set,
 * and it can change after giveaway_type was loaded.
 */
export function isFirstEntries(giveaway: Giveaway, types: GiveawayType[] = []): boolean {
  const type =
    types.find((candidate) => candidate.id === giveaway.giveaway_type_id) ?? giveaway.giveaway_type
  return type?.first_entries ?? false
}

/** What an entry option offers: "Claim" for first-entries giveaways, else "+N". */
/** Whether the current user has done this option (referred someone, for referrals). */
export function isObjectiveDone(objective: GiveawayObjective): boolean {
  return objective.is_referral ? objective.referral_count > 0 : objective.is_entered
}

/**
 * What an entry option offers. In a draw, "+N"; in a first-come giveaway,
 * "Claim" — or "Required" when every option has to be done for the key.
 */
export function entryOptionLabel(
  objective: GiveawayObjective,
  firstEntries: boolean,
  requireAll = false,
): string {
  if (objective.is_referral) {
    if (!firstEntries) return `+${objective.entries_worth ?? 0} each`
    if (objective.referral_count > 0) return requireAll ? 'Done ✓' : 'Claimed'
    return requireAll ? 'Required' : 'Refer to claim'
  }
  if (firstEntries) return requireAll ? 'Required' : 'Claim'
  return `+${objective.entries_worth ?? 0}`
}

/**
 * Where the current user stands on a referral option: "3 referred · +30
 * entries", or in a first-come giveaway what referring does for their key.
 */
export function referralSummary(
  objective: GiveawayObjective,
  firstEntries = false,
  requireAll = false,
): string {
  const count = objective.referral_count
  if (firstEntries) {
    if (count === 0) {
      return requireAll
        ? 'Copy your link — refer someone to complete this option'
        : 'Copy your link — your first referral earns you a key'
    }
    return `${requireAll ? 'Done' : 'Key earned'} · ${count.toLocaleString()} referred`
  }
  if (count === 0) return 'Copy your link and share it'
  const earned = count * (objective.entries_worth ?? 0)
  return `${count.toLocaleString()} referred · +${earned.toLocaleString()} entries`
}

export function giveawayListFrom(data: unknown): Giveaway[] {
  return (asArray(data) ?? []).map((item) => new Giveaway(item))
}
